/* eslint-disable max-lines */
import {
  Box,
  Button,
  Flex,
  Paper,
  Stack,
  Text,
  Transition,
} from '@mantine/core';
import { useHover, usePrevious } from '@mantine/hooks';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MarkdownRenderer from './markdown-renderer';

import classes from '../chat.module.css';
import { ChatStatusHelpers } from '../constants';
import {
  findPrecedingUserMessageId,
  findPrecedingUserQuestion,
  shouldFilterIncompleteMessage,
} from '../utils/chat.utils';

import type {
  ChatStatus,
  ChatUIMessage,
  CitationSource,
  StreamingCitationSources,
} from '../types';
import type { MarkdownRendererRef } from './markdown-renderer';
import type { MarkdownRendererContextValue } from './markdown-renderer/MarkdownRenderer.context';
import type { ReactNode } from 'react';

/** Context passed to render delegate callbacks. */
export type MessageRenderContext = {
  isCurrentMessage: boolean;
  status?: ChatStatus;
  sessionId: string;
};

/** Context passed to the renderMessageActions callback. */
export type MessageActionsContext = {
  message: ChatUIMessage;
  messageText: string;
  sources: CitationSource[];
  sessionId: string;
  messageId: string;
  userMessageId?: string;
  isHovered: boolean;
  userQuestion?: string;
  getRenderedHtml: () => string;
};

export type ChatStreamProps = {
  messages: ChatUIMessage[];
  status: ChatStatus;
  onStop: () => void;
  error?: Error;
  regenerate: () => void;
  sessionId: string;
  headerHeight?: number;
  bodyPadding?: number;
  emptyStateMessage?: string;
  emptyStateContent?: ReactNode;
  /** When true, streaming messages expand to fill viewport. Set false for embedded contexts. */
  useViewportMinHeight?: boolean;
  /** Optional className to apply to the chat stream container */
  className?: string;
  /** Extra part types that count as meaningful content when filtering incomplete messages. */
  extraPartTypes?: string[];
  /** Render content above the message text (e.g. DSQ text_selection_context preview). */
  renderMessageHeader?: (
    message: ChatUIMessage,
    context: MessageRenderContext,
  ) => ReactNode | null;
  /** Override the entire message rendering. Return null to use default rendering. */
  renderMessage?: (
    message: ChatUIMessage,
    context: MessageRenderContext,
  ) => ReactNode | null;
  /** Render content after the message text (e.g. followups). */
  renderMessageFooter?: (
    message: ChatUIMessage,
    context: MessageRenderContext,
  ) => ReactNode | null;
  /**
   * Render message actions (copy, share, etc.) below the message.
   * When not provided, no actions are shown.
   */
  renderMessageActions?: (context: MessageActionsContext) => ReactNode | null;
  /**
   * Render indicators (tool calls, status reports, etc.) above the message content.
   * When not provided, no indicators are shown.
   */
  renderIndicators?: (context: {
    message: ChatUIMessage;
    status?: ChatStatus;
    isCurrentMessage?: boolean;
  }) => ReactNode | null;
  /** Override citation popover content rendering. Passed to MarkdownRendererContext. */
  renderCitationContentOverride?: MarkdownRendererContextValue['renderCitationContentOverride'];
};

const BOTTOM_MARGIN = 50;
const CHAT_MESSAGE_APPROX_SIZE = 58 + 24 * 2 + BOTTOM_MARGIN;
const CHAT_INPUT_APPROX_SIZE = 173;

const PENDING_ASSISTANT_PLACEHOLDER_ID = '__pending_assistant__';
const PENDING_ASSISTANT_PLACEHOLDER: ChatUIMessage = {
  id: PENDING_ASSISTANT_PLACEHOLDER_ID,
  role: 'assistant',
  parts: [],
};

type AnimatedMessageWrapperProps = {
  children: ReactNode;
  shouldAnimate: boolean;
};

function AnimatedMessageWrapper({
  children,
  shouldAnimate,
}: AnimatedMessageWrapperProps) {
  const [mounted, setMounted] = useState(!shouldAnimate);

  useEffect(() => {
    if (shouldAnimate && !mounted) {
      const timer = requestAnimationFrame(() => {
        setMounted(true);
      });
      return () => cancelAnimationFrame(timer);
    }
  }, [shouldAnimate, mounted]);

  if (!shouldAnimate) {
    return <>{children}</>;
  }

  return (
    <Transition
      mounted={mounted}
      transition="fade"
      duration={250}
      timingFunction="ease-out"
    >
      {(styles) => <div style={styles}>{children}</div>}
    </Transition>
  );
}

export default function ChatStream({
  messages,
  status,
  onStop,
  error,
  regenerate,
  sessionId,
  headerHeight = 80,
  bodyPadding = 16,
  emptyStateMessage = 'Start a conversation with Klue Assistant',
  emptyStateContent,
  useViewportMinHeight = true,
  className,
  extraPartTypes,
  renderMessageHeader,
  renderMessage,
  renderMessageFooter,
  renderMessageActions,
  renderIndicators,
  renderCitationContentOverride,
}: ChatStreamProps) {
  const initialMessagesRef = useRef<Set<string>>(new Set());
  const previousSessionId = usePrevious(sessionId);
  const hasInitializedForSession = useRef<string | null>(null);

  useEffect(() => {
    if (previousSessionId !== sessionId) {
      initialMessagesRef.current = new Set();
      hasInitializedForSession.current = null;
    }

    if (hasInitializedForSession.current !== sessionId && messages.length > 0) {
      initialMessagesRef.current = new Set(messages.map((m) => m.id));
      hasInitializedForSession.current = sessionId;
    }
  }, [sessionId, messages, previousSessionId]);

  const assistantMessages = messages.filter((msg) => msg.role === 'assistant');
  const lastAssistantMessageId =
    assistantMessages[assistantMessages.length - 1]?.id;

  const filteredMessages = messages.filter((message) => {
    const isLastAssistantMessage = message.id === lastAssistantMessageId;
    return !shouldFilterIncompleteMessage(
      message,
      status,
      isLastAssistantMessage,
      extraPartTypes,
    );
  });

  const chatMessageApproxSize =
    CHAT_MESSAGE_APPROX_SIZE + headerHeight + bodyPadding;

  const isEmpty = filteredMessages.length === 0;

  /**
   * Whether to render a placeholder assistant bubble while the backend is
   * working but has not yet produced an assistant message (e.g. DSQ / Entity
   * Graph prefetch before `RUN_STARTED`). Reuses `renderIndicators` so modules
   * keep their own icon / registry and consistent "Preparing" copy.
   */
  const lastFilteredMessage = filteredMessages[filteredMessages.length - 1];
  const isPendingAssistantResponse =
    !!renderIndicators &&
    ChatStatusHelpers.isProcessing(status) &&
    (!lastFilteredMessage || lastFilteredMessage.role === 'user');

  return (
    <Stack
      gap="xl"
      mb={BOTTOM_MARGIN}
      flex={isEmpty ? 1 : undefined}
      className={className}
    >
      {isEmpty &&
        (emptyStateContent ? (
          emptyStateContent
        ) : (
          <Box ta="center" py="xl">
            <Text c="dimmed">{emptyStateMessage}</Text>
          </Box>
        ))}

      {filteredMessages.map((message, index) => {
        const isLastAssistantMessage = message.id === lastAssistantMessageId;
        const isStreamingLastMessage =
          isLastAssistantMessage &&
          message.role === 'assistant' &&
          ChatStatusHelpers.isStreaming(status);
        const isNewMessage = !initialMessagesRef.current.has(message.id);

        const precedingUserQuestion =
          message.role === 'assistant'
            ? findPrecedingUserQuestion(filteredMessages, index)
            : undefined;

        const precedingUserMsgId =
          message.role === 'assistant'
            ? findPrecedingUserMessageId(filteredMessages, index)
            : undefined;

        return (
          <AnimatedMessageWrapper key={message.id} shouldAnimate={isNewMessage}>
            <Box
              mih={
                isStreamingLastMessage && useViewportMinHeight
                  ? `calc(100vh - ${chatMessageApproxSize}px - ${CHAT_INPUT_APPROX_SIZE}px)`
                  : undefined
              }
            >
              <Paper
                maw={message.role === 'user' ? '80%' : '100%'}
                className={classes.chatMessageContainer}
                shadow="sm"
                data-role={message.role}
              >
                <div className={classes.chatMessage}>
                  <Message
                    message={message}
                    status={status}
                    isCurrentMessage={isLastAssistantMessage}
                    sessionId={sessionId}
                    userQuestion={precedingUserQuestion}
                    userMessageId={precedingUserMsgId}
                    renderMessageHeader={renderMessageHeader}
                    renderMessage={renderMessage}
                    renderMessageFooter={renderMessageFooter}
                    renderMessageActions={renderMessageActions}
                    renderIndicators={renderIndicators}
                    renderCitationContentOverride={
                      renderCitationContentOverride
                    }
                  />
                </div>
              </Paper>
            </Box>
          </AnimatedMessageWrapper>
        );
      })}

      {isPendingAssistantResponse && (
        <Box
          mih={
            useViewportMinHeight
              ? `calc(100vh - ${chatMessageApproxSize}px - ${CHAT_INPUT_APPROX_SIZE}px)`
              : undefined
          }
        >
          <Paper
            maw="100%"
            className={classes.chatMessageContainer}
            shadow="sm"
            data-role="assistant"
          >
            <div className={classes.chatMessage}>
              {renderIndicators?.({
                message: PENDING_ASSISTANT_PLACEHOLDER,
                status,
                isCurrentMessage: true,
              })}
            </div>
          </Paper>
        </Box>
      )}

      {error && (
        <Paper p="md" bg="yellow.1" className={classes.chatMessage} shadow="sm">
          <Flex align="center" gap="xs">
            <Text>{error.message}</Text>
            <Flex flex={{ shrink: 0 }}>
              <Button
                variant="filled"
                size="xs"
                onClick={() => regenerate()}
                flex={{ shrink: 0 }}
              >
                Retry
              </Button>
            </Flex>
          </Flex>
        </Paper>
      )}
    </Stack>
  );
}

type MessageProps = {
  message: ChatUIMessage;
  status?: ChatStatus;
  isCurrentMessage?: boolean;
  sessionId: string;
  userQuestion?: string;
  userMessageId?: string;
  renderMessageHeader?: (
    message: ChatUIMessage,
    context: MessageRenderContext,
  ) => ReactNode | null;
  renderMessage?: (
    message: ChatUIMessage,
    context: MessageRenderContext,
  ) => ReactNode | null;
  renderMessageFooter?: (
    message: ChatUIMessage,
    context: MessageRenderContext,
  ) => ReactNode | null;
  renderMessageActions?: (context: MessageActionsContext) => ReactNode | null;
  renderIndicators?: (context: {
    message: ChatUIMessage;
    status?: ChatStatus;
    isCurrentMessage?: boolean;
  }) => ReactNode | null;
  renderCitationContentOverride?: MarkdownRendererContextValue['renderCitationContentOverride'];
};

const Message = memo(
  function Message({
    message,
    status,
    isCurrentMessage,
    sessionId,
    userQuestion,
    userMessageId,
    renderMessageHeader,
    renderMessage,
    renderMessageFooter,
    renderMessageActions,
    renderIndicators,
    renderCitationContentOverride,
  }: MessageProps) {
    const renderContext: MessageRenderContext = useMemo(
      () => ({
        isCurrentMessage: isCurrentMessage ?? false,
        status,
        sessionId,
      }),
      [isCurrentMessage, status, sessionId],
    );

    const messageParts = message.parts;

    const sources = useMemo((): StreamingCitationSources => {
      const sourcesPart = messageParts.find(
        (part) => part.type === 'data-citation_sources',
      );
      // AGUITransport sends progressively updated sparse array during streaming
      return (
        (sourcesPart as { data: StreamingCitationSources } | undefined)?.data ??
        []
      );
    }, [messageParts]);

    const resolvedSources = useMemo(
      () => sources.filter((s): s is CitationSource => s !== undefined),
      [sources],
    );

    const messageText = message.parts
      ?.filter((part) => part.type === 'text')
      .map((part) => part.text)
      .join('\n');

    const { hovered, ref: hoverRef } = useHover();

    const markdownRefsMap = useRef<Map<number, MarkdownRendererRef>>(new Map());

    const setMarkdownRef = useCallback(
      (index: number) => (ref: MarkdownRendererRef | null) => {
        if (ref) {
          markdownRefsMap.current.set(index, ref);
        } else {
          markdownRefsMap.current.delete(index);
        }
      },
      [],
    );

    const getRenderedHtml = useCallback(() => {
      const htmlParts: string[] = [];
      const sortedKeys = Array.from(markdownRefsMap.current.keys()).sort(
        (a, b) => a - b,
      );
      for (const key of sortedKeys) {
        const ref = markdownRefsMap.current.get(key);
        if (ref) {
          htmlParts.push(ref.getHtmlContent());
        }
      }
      return htmlParts.join('');
    }, []);

    const isStreaming =
      isCurrentMessage && status && ChatStatusHelpers.isStreaming(status);

    // Allow modules to completely override message rendering (e.g. GuidedFlowThinking)
    const messageOverride = renderMessage?.(message, renderContext);
    if (messageOverride != null) {
      return <>{messageOverride}</>;
    }

    return (
      <Box ref={hoverRef}>
        {renderIndicators?.({ message, status, isCurrentMessage })}
        {renderMessageHeader?.(message, renderContext)}
        {message.parts?.map((part, index) => {
          // Non-text parts are handled by header/footer render delegates
          if (part.type !== 'text') return null;
          const textPart = part as { type: 'text'; text: string };
          if (message.role === 'assistant') {
            return (
              <MarkdownRenderer
                key={index}
                ref={setMarkdownRef(index)}
                content={textPart.text}
                sources={sources}
                renderCitationContentOverride={renderCitationContentOverride}
              />
            );
          }
          return (
            <Text key={index} style={{ whiteSpace: 'pre-wrap' }}>
              {textPart.text.replace(/\n{3,}/g, '\n\n')}
            </Text>
          );
        })}
        {renderMessageFooter?.(message, renderContext)}
        {message.role === 'assistant' &&
          messageText &&
          !isStreaming &&
          renderMessageActions?.({
            message,
            messageText,
            sources: resolvedSources,
            sessionId,
            // Streamed messages use AGUI runId as message.id
            // textMessageId in metadata is the AGUI messageId consistent with loaded messages
            messageId: message.metadata?.textMessageId ?? message.id,
            userMessageId,
            isHovered: hovered,
            userQuestion,
            getRenderedHtml,
          })}
      </Box>
    );
  },
  (prevProps: MessageProps, nextProps: MessageProps) => {
    // Return true = skip re-render (props are equal)
    // Return false = must re-render (props changed)

    // For the currently streaming message, always allow re-renders
    // This ensures text content and citations update in real-time
    if (nextProps.isCurrentMessage) return false;

    // Different message identity - must re-render
    if (prevProps.message.id !== nextProps.message.id) return false;

    // Status/state changes - must re-render
    if (prevProps.isCurrentMessage !== nextProps.isCurrentMessage) return false;
    if (prevProps.status !== nextProps.status) return false;
    if (prevProps.sessionId !== nextProps.sessionId) return false;
    if (prevProps.userQuestion !== nextProps.userQuestion) return false;
    if (prevProps.userMessageId !== nextProps.userMessageId) return false;

    // Parts array length changed (new parts added, e.g., citation sources)
    if (prevProps.message.parts?.length !== nextProps.message.parts?.length)
      return false;

    // For non-streaming messages, compare text content to catch any missed updates
    const getTextContent = (msg: MessageProps['message']) =>
      msg.parts
        ?.filter((p) => p.type === 'text')
        .map((p) => (p as { text: string }).text)
        .join('') ?? '';

    if (getTextContent(prevProps.message) !== getTextContent(nextProps.message))
      return false;

    return true;
  },
);
